import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';

const { FiLogIn, FiUser, FiLock, FiAlertCircle, FiMessageSquare, FiInfo } = FiIcons;

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const { login, debugInfo } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);
    try {
      await login(username, password);
      navigate('/dashboard');
    } catch (error) {
      setLoginError('Username atau password salah');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDebug = () => {
    setShowDebug(prev => !prev);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-md w-full"
      >
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-center">
          <SafeIcon icon={FiMessageSquare} className="text-white text-4xl mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white">Monitoring Dashboard</h1>
          <p className="text-blue-100 mt-1">AI Agent Conversation Monitoring</p>
        </div>

        <div className="p-6 space-y-6">
          <h2 className="text-xl font-semibold text-gray-800 text-center">Login</h2>

          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center space-x-2">
              <SafeIcon icon={FiAlertCircle} className="text-red-500" />
              <span>{loginError}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiUser} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Masukkan username"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiLock} className="text-gray-400" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Masukkan password"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Logging in...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiLogIn} />
                  <span>Login</span>
                </>
              )}
            </button>
          </form>

          <div className="text-center text-sm text-gray-500 pt-4 border-t">
            <p>Default credentials:</p>
            <p>Admin: username: <code>admin</code> / password: <code>YHH0KTNz9l</code></p>
            <p>Viewer: username: <code>viewer</code> / password: <code>xd^u2%$D</code></p>

            <button
              onClick={toggleDebug}
              className="mt-4 text-blue-500 hover:text-blue-700 flex items-center justify-center mx-auto"
            >
              <SafeIcon icon={FiInfo} className="mr-1" />
              <span>{showDebug ? 'Hide Debug Info' : 'Show Debug Info'}</span>
            </button>

            {showDebug && debugInfo && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-left">
                <h4 className="font-medium text-gray-700 mb-2">Debug Information:</h4>
                <pre className="text-xs overflow-auto p-2 bg-gray-100 rounded max-h-40">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;